import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, TextProps as RNTextProps } from "react-native";
import { useI18n } from "@lookiero/i18n-react";
import { Checkbox, Text } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { ReturnQuestionType } from "../../../../../../../projection/returnQuestion/returnQuestion.constants";
import { useStaticInfo } from "../../../../../hooks/useStaticInfo";
import { DOMAIN } from "../../../../../i18n/i18n";
import {
  useReturnQuestionFeedback,
  useReturnQuestionFeedbackForReturnQuestion,
} from "../../behaviors/useReturnQuestionFeedback";
import {
  deepestReturnQuestionWithFeedbackForReturnQuestion,
  feedbackForReturnQuestion,
} from "../../util/returnQuestionFeedback";
import { ReturnQuestionItem } from "../ReturnQuestionItem";
import { ModalOptionReturnQuestionItems } from "../modalOptionReturnQuestionItems/ModalOptionReturnQuestionItems";
import { style as radioReturnQuestionItemStyle } from "./RadioReturnQuestionItem.style";

const TEXT: Record<Tradename, FC<RNTextProps>> = {
  [Tradename.LOOKIERO]: (props) => <Text level={1} variant="detail" {...props} />,
  [Tradename.OUTFITTERY]: (props) => <Text level={3} {...props} />,
};

const RadioReturnQuestionItem: ReturnQuestionItem<ReturnQuestionType.OPTION> = ({
  returnQuestion,
  returnQuestionParent,
  testID,
  portalHostName,
}) => {
  const { tradename } = useStaticInfo();

  const style = useMemo(() => radioReturnQuestionItemStyle(), []);

  const { formatMessage } = useI18n({ domain: DOMAIN });
  const [modalVisible, setModalVisible] = useState(false);
  const {
    feedback: feedbackId,
    onChange,
    clear,
  } = useReturnQuestionFeedbackForReturnQuestion({ returnQuestion: returnQuestionParent });
  const feedback = useReturnQuestionFeedback();
  const translate = useCallback(
    (returnQuestionName: string) => formatMessage({ id: returnQuestionName, defaultMessage: returnQuestionName }),
    [formatMessage],
  );

  const inputValue = feedbackForReturnQuestion({ feedback, returnQuestion, translate }).join(" / ");
  const optionText = formatMessage({ id: returnQuestion.translationKey });

  const hasChildren = Boolean(returnQuestion.children && returnQuestion.children.length > 0);

  const handleOnModalClose = useCallback(() => {
    setModalVisible(false);
  }, []);

  const handleOnPress = useCallback(() => {
    clear();
    onChange({ returnQuestionId: returnQuestionParent.id, returnQuestionFeedback: returnQuestion.id });

    if (hasChildren) {
      setModalVisible(true);
    }
  }, [clear, hasChildren, onChange, returnQuestion.id, returnQuestionParent.id]);

  const [deepestReturnQuestionWithFeedback, isLeaf] = deepestReturnQuestionWithFeedbackForReturnQuestion({
    feedback,
    returnQuestion,
  });
  useEffect(() => {
    if (deepestReturnQuestionWithFeedback && isLeaf) {
      setModalVisible(false);
    }
  }, [deepestReturnQuestionWithFeedback, isLeaf]);

  const checked = returnQuestion.id === feedbackId;

  const Text = useMemo(() => TEXT[tradename], [tradename]);

  return (
    <>
      <Pressable
        accessibilityLabel="radio-return-question-item"
        accessibilityState={{ checked }}
        testID={testID}
        style={[
          style.wrapper,
          tradename === Tradename.LOOKIERO && style.wrapperLookiero,
          tradename === Tradename.LOOKIERO && checked && style.wrapperLookieroChecked,
        ]}
        accessible
        onPress={handleOnPress}
      >
        <Checkbox checked={checked} />

        <Text style={style.text}>{inputValue ? `${optionText} / ${inputValue}` : `${optionText}`}</Text>
      </Pressable>

      {hasChildren ? (
        <ModalOptionReturnQuestionItems
          portalHostName={portalHostName}
          returnQuestion={returnQuestion}
          returnQuestionParent={returnQuestion}
          visible={modalVisible}
          onClose={handleOnModalClose}
        />
      ) : null}
    </>
  );
};

export { RadioReturnQuestionItem };
